// RCPT1-S7 (docs/build-briefs/RCPT1-S7-printers-settings-screen.md; PRD P0.8). Settings > Location > Receipt printers: the location's
// printers with Online or Offline, Add printer, Change password, Print test page and Unpair.
//
// Over RCPT1-S2's printer routes, RCPT1-S2-F1's password route and the test-page route. The location is chosen from the locations the
// server lists (this device's location first), never typed. Visible only to a session holding printer.manage (resolved permissions,
// never a role name); without it nothing is drawn and no printers request is sent.
//
// A PAIRING CODE OR PASSWORD IS SHOWN ONCE. It lives in one variable of the open sheet, is drawn into that sheet with textContent,
// and is erased when the sheet closes, when the section resets (sign-out) and when the admin teardown dismisses sheets. It is never
// written to storage, the URL, the console or any list state. Copy uses the clipboard from the tap itself. All text via textContent.
// Admin surface: every control is 44px or taller.

import {
  addPrinter,
  changePrinterPassword,
  listPrinters,
  listTenantLocations,
  printTestPage,
  unpairPrinterRequest,
  type PrinterDto,
  type TenantLocationNameDto,
} from "./api.ts";
import { showConsoleToast, clearConsoleToast } from "./consoleToast.ts";
import { sortByName } from "./nameSort.ts";
import {
  ADD_LABEL,
  CODE_LIFE_LINE,
  NONE_LINE,
  ONCE_LINE,
  SECTION_TITLE,
  SETUP_STEPS,
  cloudPrntUrl,
  jobsLine,
  printerActions,
  printerBadge,
  printerFlags,
  unpairTitle,
  validName,
  visiblePrinters,
} from "./printersModel.ts";

export interface PrintersSectionDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds printer.manage. */
  canManage: () => boolean;
  /** The location this device belongs to, when it is set up as one. */
  deviceLocation: () => { id: string; name: string } | undefined;
  /** True while the session this section serves is still the live one. */
  isCurrent: () => boolean;
  newKey: () => string;
  lockScroll: () => void;
  unlockScroll: () => void;
}

export interface PrintersSection {
  refresh(): Promise<void>;
  /** Forgets everything, including any code on screen. */
  reset(): void;
  /** Closes any open sheet and erases a code from the page (sign-out teardown). */
  dismissSheets(): void;
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string | null,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);
  if (className !== null) created.className = className;
  if (text !== undefined) created.textContent = text;
  return created;
}

function button(label: string, cls: string, testid: string, onTap: () => void): HTMLButtonElement {
  const created = node("button", cls, label);
  created.type = "button";
  created.dataset["testid"] = testid;
  created.addEventListener("click", onTap);
  return created;
}

function messageOf(body: unknown): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === "string" && message.length > 0
    ? message
    : "Something went wrong. Try again.";
}

const ID_PREFIX = "admin-printers";

type Sheet =
  | { kind: "add"; name: string; error: string; busy: boolean; key: string }
  | {
      kind: "code";
      title: string;
      what: "Pairing code" | "Password";
      /** The one-time secret. Held here and nowhere else. */
      secret: string;
      url: string;
    }
  | { kind: "unpair"; printer: PrinterDto; error: string; busy: boolean; key: string };

export function mountPrintersSection(
  root: HTMLElement,
  deps: PrintersSectionDeps,
): PrintersSection {
  let locations: { id: string; name: string }[] = [];
  let locationId: string | undefined;
  let printers: PrinterDto[] = [];
  let loaded = false;
  let statusLine = "";
  let errorLine = "";
  let busyId: string | undefined;
  let sheet: Sheet | undefined;
  let sheetHost: HTMLElement | undefined;
  let loadSequence = 0;
  root.hidden = true;

  function closeSheet(): void {
    if (sheet !== undefined) deps.unlockScroll();
    // The one-time secret goes with the sheet.
    sheet = undefined;
    sheetHost?.remove();
    sheetHost = undefined;
    clearConsoleToast();
  }

  function reset(): void {
    loadSequence += 1;
    closeSheet();
    locations = [];
    locationId = undefined;
    printers = [];
    loaded = false;
    statusLine = "";
    errorLine = "";
    busyId = undefined;
    root.replaceChildren();
    root.hidden = true;
  }

  async function loadLocations(): Promise<void> {
    const listed = await listTenantLocations(deps.apiBaseUrl(), deps.tenantId());
    const device = deps.deviceLocation();
    if (listed.status === 200 && Array.isArray(listed.body.locations)) {
      const found = listed.body.locations as TenantLocationNameDto[];
      locations = found.map((siteLocation) => ({ id: siteLocation.id, name: siteLocation.name }));
      const preferred =
        (device !== undefined &&
          locations.find((siteLocation) => siteLocation.id === device.id)?.id) ||
        found.find((siteLocation) => siteLocation.isMain)?.id ||
        locations[0]?.id;
      locationId =
        locationId !== undefined && locations.some((siteLocation) => siteLocation.id === locationId)
          ? locationId
          : preferred;
      return;
    }
    // The location list needs a right this session may not hold: the device's own location is still known.
    locations = device === undefined ? [] : [device];
    locationId = device?.id;
  }

  async function load(): Promise<void> {
    if (!deps.canManage()) {
      reset();
      return;
    }
    const sequence = (loadSequence += 1);
    if (locations.length === 0) {
      await loadLocations();
      if (sequence !== loadSequence || !deps.isCurrent()) return;
    }
    if (locationId === undefined) {
      reset();
      return;
    }
    const result = await listPrinters(deps.apiBaseUrl(), deps.tenantId(), locationId);
    if (sequence !== loadSequence || !deps.isCurrent()) return;
    if (result.status === 200 && Array.isArray(result.body.printers)) {
      printers = visiblePrinters(result.body.printers);
      loaded = true;
      render();
      return;
    }
    if (result.status === 403 || result.status === 404) {
      reset();
      return;
    }
    loaded = false;
    errorLine = `Could not load printers. ${messageOf(result.body)}`;
    render();
  }

  function render(): void {
    // A re-render keeps an open sheet: it is drawn again after the content.
    const keepSheet = sheet;
    sheetHost = undefined;
    root.replaceChildren();
    root.hidden = false;
    const card = node("section", "tablets-card");
    card.dataset["testid"] = `${ID_PREFIX}-card`;
    const head = node("div", "tablets-card-head");
    head.append(node("h2", "tablets-title", SECTION_TITLE));
    card.append(head);

    if (locations.length > 1) {
      const chips = node("div", "rt-locations");
      chips.setAttribute("role", "group");
      chips.setAttribute("aria-label", "Which location");
      for (const siteLocation of sortByName(locations, (listed) => listed.name)) {
        const chip = node("button", "tablets-chip", siteLocation.name);
        chip.type = "button";
        chip.dataset["testid"] = `${ID_PREFIX}-location`;
        chip.dataset["locationId"] = siteLocation.id;
        chip.setAttribute("aria-pressed", String(siteLocation.id === locationId));
        chip.addEventListener("click", () => {
          if (siteLocation.id === locationId) return;
          locationId = siteLocation.id;
          statusLine = "";
          errorLine = "";
          void load();
        });
        chips.append(chip);
      }
      card.append(chips);
    } else if (locations.length === 1 && locations[0] !== undefined) {
      const name = node("p", "rt-location-name", locations[0].name);
      name.dataset["testid"] = `${ID_PREFIX}-location-name`;
      card.append(name);
    }

    if (loaded) {
      if (printers.length === 0) {
        const empty = node("p", "tablets-empty", NONE_LINE);
        empty.dataset["testid"] = `${ID_PREFIX}-empty`;
        card.append(empty);
      }
      for (const printer of printers) card.append(printerRow(printer));
      const actions = node("div", "pr-actions");
      actions.append(
        button(ADD_LABEL, "tablets-primary", `${ID_PREFIX}-add`, () => openAdd()),
        button("Refresh", "tablets-secondary", `${ID_PREFIX}-refresh`, () => void refreshNow()),
      );
      card.append(actions);
    }
    if (statusLine !== "") {
      const status = node("p", "admin-settings-save-status", statusLine);
      status.setAttribute("role", "status");
      status.dataset["testid"] = `${ID_PREFIX}-status`;
      card.append(status);
    }
    if (errorLine !== "") {
      const error = node("p", "admin-error", errorLine);
      error.setAttribute("role", "alert");
      error.dataset["testid"] = `${ID_PREFIX}-error`;
      card.append(error);
    }
    root.append(card);
    if (keepSheet !== undefined) drawSheet();
  }

  function printerRow(printer: PrinterDto): HTMLElement {
    const row = node("div", "tablets-row");
    row.dataset["testid"] = `${ID_PREFIX}-row`;
    row.dataset["printerId"] = printer.id;
    const text = node("div", "tablets-row-text");
    const name = node("div", "tablets-row-name");
    name.append(node("span", null, printer.name));
    const badge = printerBadge(printer);
    const pill = node("span", `so-badge so-badge-${badge.tone}`);
    pill.dataset["testid"] = `${ID_PREFIX}-state`;
    pill.append(node("span", "so-badge-dot"), badge.label);
    name.append(pill);
    for (const flag of printerFlags(printer)) {
      const tag = node("span", "cp-tag", flag);
      tag.dataset["testid"] = `${ID_PREFIX}-flag`;
      name.append(tag);
    }
    text.append(name);
    const jobs = jobsLine(printer);
    if (jobs !== "") text.append(node("div", "tablets-row-line", jobs));
    row.append(text);

    const allowed = printerActions(printer);
    const busy = busyId === printer.id;
    const actions = node("div", "tablets-row-actions pr-row-actions");
    if (allowed.testPage) {
      const test = button(
        "Print test page",
        "tablets-secondary",
        `${ID_PREFIX}-test`,
        () => void sendTestPage(printer),
      );
      test.disabled = busy;
      actions.append(test);
    }
    if (allowed.changePassword) {
      const change = button(
        "Change password",
        "tablets-secondary",
        `${ID_PREFIX}-change-password`,
        () => void changePassword(printer),
      );
      change.disabled = busy;
      actions.append(change);
    }
    if (allowed.unpair) {
      const unpair = button("Unpair", "tablets-danger-quiet", `${ID_PREFIX}-unpair`, () =>
        openUnpair(printer),
      );
      unpair.disabled = busy;
      actions.append(unpair);
    }
    row.append(actions);
    return row;
  }

  async function refreshNow(): Promise<void> {
    statusLine = "";
    errorLine = "";
    await load();
  }

  // ---------------------------------------------------------------------
  // Actions

  async function sendTestPage(printer: PrinterDto): Promise<void> {
    if (busyId !== undefined) return;
    busyId = printer.id;
    statusLine = "";
    errorLine = "";
    render();
    const result = await printTestPage(deps.apiBaseUrl(), deps.tenantId(), printer.id, {
      idempotencyKey: deps.newKey(),
    });
    if (!deps.isCurrent()) return;
    busyId = undefined;
    if (result.status === 200 || result.status === 201) statusLine = "Test page sent";
    else errorLine = messageOf(result.body);
    render();
  }

  async function changePassword(printer: PrinterDto): Promise<void> {
    if (busyId !== undefined) return;
    busyId = printer.id;
    statusLine = "";
    errorLine = "";
    render();
    const result = await changePrinterPassword(deps.apiBaseUrl(), deps.tenantId(), printer.id, {
      idempotencyKey: deps.newKey(),
    });
    if (!deps.isCurrent()) return;
    busyId = undefined;
    const secret =
      result.status === 200 || result.status === 201 ? result.body.password : undefined;
    if (typeof secret === "string" && secret !== "") {
      showSecret(`New password for ${printer.name}`, "Password", secret, printer.id);
      return;
    }
    errorLine =
      result.status === 200 || result.status === 201
        ? "Password already shown. Try again."
        : messageOf(result.body);
    render();
  }

  /** Opens the one-time sheet. The secret is passed straight in and kept nowhere else. */
  function showSecret(
    title: string,
    what: "Pairing code" | "Password",
    secret: string,
    printerId: string,
    cloudPrntPath?: string,
  ): void {
    const path =
      cloudPrntPath ?? `/tenants/${encodeURIComponent(deps.tenantId())}/cloudprnt/${printerId}`;
    const url = cloudPrntUrl(deps.apiBaseUrl(), path, window.location.origin);
    openSheet({ kind: "code", title, what, secret, url });
  }

  function openAdd(): void {
    openSheet({ kind: "add", name: "", error: "", busy: false, key: deps.newKey() });
  }

  async function submitAdd(current: Extract<Sheet, { kind: "add" }>): Promise<void> {
    if (current.busy || locationId === undefined) return;
    const checked = validName(current.name);
    if (!checked.ok) {
      sheet = { ...current, error: checked.error };
      drawSheet();
      return;
    }
    sheet = { ...current, error: "", busy: true };
    drawSheet();
    const result = await addPrinter(deps.apiBaseUrl(), deps.tenantId(), {
      idempotencyKey: current.key,
      locationId: locationId,
      name: checked.name,
    });
    if (!deps.isCurrent()) return;
    if (result.status === 201 || result.status === 200) {
      const code = result.body.pairingCode;
      closeSheet();
      void load();
      if (typeof code === "string" && code !== "") {
        showSecret(
          `Pair ${result.body.printer.name}`,
          "Pairing code",
          code,
          result.body.printer.id,
          result.body.cloudPrntPath,
        );
      } else {
        errorLine = "Code already shown. Unpair the printer and add it again.";
        render();
      }
      return;
    }
    if (sheet?.kind === "add") {
      sheet = { ...sheet, busy: false, error: messageOf(result.body) };
      drawSheet();
    }
  }

  function openUnpair(printer: PrinterDto): void {
    openSheet({ kind: "unpair", printer, error: "", busy: false, key: deps.newKey() });
  }

  async function confirmUnpair(current: Extract<Sheet, { kind: "unpair" }>): Promise<void> {
    if (current.busy) return;
    sheet = { ...current, busy: true, error: "" };
    drawSheet();
    const result = await unpairPrinterRequest(
      deps.apiBaseUrl(),
      deps.tenantId(),
      current.printer.id,
      { idempotencyKey: current.key },
    );
    if (!deps.isCurrent()) return;
    if (result.status === 200) {
      closeSheet();
      statusLine = `${current.printer.name} unpaired`;
      errorLine = "";
      await load();
      return;
    }
    if (sheet?.kind === "unpair") {
      sheet = { ...sheet, busy: false, error: messageOf(result.body) };
      drawSheet();
    }
  }

  // ---------------------------------------------------------------------
  // Sheets

  function openSheet(next: Sheet): void {
    if (sheet === undefined) deps.lockScroll();
    sheet = next;
    drawSheet();
  }

  function drawSheet(): void {
    if (sheet === undefined) return;
    if (sheetHost === undefined) {
      sheetHost = node("div", "tablets-scrim");
      sheetHost.dataset["testid"] = `${ID_PREFIX}-scrim`;
      root.append(sheetHost);
    }
    sheetHost.replaceChildren();
    const content =
      sheet.kind === "add"
        ? addSheet(sheet)
        : sheet.kind === "code"
          ? codeSheet(sheet)
          : unpairSheet(sheet);
    sheetHost.append(content);
    const focusTarget =
      content.querySelector<HTMLElement>("input") ?? content.querySelector<HTMLElement>("button");
    focusTarget?.focus({ preventScroll: true });
  }

  function shell(title: string, testid: string): HTMLElement {
    const titleId = `${testid}-title`;
    const sheetEl = node("div", "tablets-sheet");
    sheetEl.dataset["testid"] = testid;
    sheetEl.setAttribute("role", "dialog");
    sheetEl.setAttribute("aria-modal", "true");
    sheetEl.setAttribute("aria-labelledby", titleId);
    const heading = node("h2", null, title);
    heading.id = titleId;
    sheetEl.append(heading);
    sheetEl.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        closeSheet();
      }
    });
    return sheetEl;
  }

  function errorBlock(text: string): HTMLElement {
    const error = node("p", "admin-error", text);
    error.setAttribute("role", "alert");
    error.dataset["testid"] = `${ID_PREFIX}-sheet-error`;
    return error;
  }

  function addSheet(current: Extract<Sheet, { kind: "add" }>): HTMLElement {
    const sheetEl = shell("Add printer", `${ID_PREFIX}-add-sheet`);
    const field = node("div", "tablets-field");
    const label = node("label", "tablets-label", "Name");
    label.htmlFor = `${ID_PREFIX}-name`;
    const input = node("input", "field-input");
    input.type = "text";
    input.id = `${ID_PREFIX}-name`;
    input.dataset["testid"] = `${ID_PREFIX}-name`;
    input.autocomplete = "off";
    input.maxLength = 60;
    input.value = current.name;
    input.disabled = current.busy;
    input.addEventListener("input", () => {
      current.name = input.value;
    });
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") void submitAdd(current);
    });
    field.append(label, input);
    sheetEl.append(field);
    if (current.error !== "") sheetEl.append(errorBlock(current.error));
    const foot = node("div", "tablets-sheet-foot");
    const cancel = button(
      "Cancel",
      "tablets-secondary tablets-sheet-button",
      `${ID_PREFIX}-add-cancel`,
      () => closeSheet(),
    );
    cancel.disabled = current.busy;
    const make = button(
      current.busy ? "Adding" : "Add",
      "tablets-primary",
      `${ID_PREFIX}-add-submit`,
      () => void submitAdd(current),
    );
    make.disabled = current.busy;
    foot.append(cancel, make);
    sheetEl.append(foot);
    return sheetEl;
  }

  async function copy(text: string, what: string, box: HTMLElement): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      showConsoleToast(`${what} copied`);
    } catch {
      // No clipboard permission: select the text so a copy shortcut still works.
      const range = document.createRange();
      range.selectNodeContents(box);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      showConsoleToast("Copy failed");
    }
  }

  function codeSheet(current: Extract<Sheet, { kind: "code" }>): HTMLElement {
    const sheetEl = shell(current.title, `${ID_PREFIX}-code-sheet`);
    const steps = node("ol", "pr-steps");
    steps.dataset["testid"] = `${ID_PREFIX}-steps`;
    for (const step of SETUP_STEPS) steps.append(node("li", null, step));
    sheetEl.append(steps);

    const urlLabel = node("p", "tablets-label", "Server URL");
    const urlBox = node("div", "tablets-codebox pr-box");
    const urlText = node("p", "pr-url", current.url);
    urlText.dataset["testid"] = `${ID_PREFIX}-url`;
    urlBox.append(urlText);
    const urlCopy = button(
      "Copy URL",
      "tablets-secondary",
      `${ID_PREFIX}-copy-url`,
      () => void copy(current.url, "URL", urlText),
    );
    sheetEl.append(urlLabel, urlBox, urlCopy);

    const codeLabel = node("p", "tablets-label", `${current.what} (${ONCE_LINE})`);
    const codeBox = node("div", "tablets-codebox pr-box");
    const codeText = node("p", "pr-code", current.secret);
    codeText.dataset["testid"] = `${ID_PREFIX}-code`;
    codeBox.append(codeText, node("p", "pr-code-life", CODE_LIFE_LINE));
    (codeBox.lastElementChild as HTMLElement).dataset["testid"] = `${ID_PREFIX}-code-life`;
    const codeCopy = button(
      `Copy ${current.what.toLowerCase()}`,
      "tablets-secondary",
      `${ID_PREFIX}-copy-code`,
      () => void copy(current.secret, current.what, codeText),
    );
    sheetEl.append(codeLabel, codeBox, codeCopy);

    const foot = node("div", "tablets-sheet-foot");
    foot.append(
      button("Done", "tablets-primary", `${ID_PREFIX}-code-done`, () => {
        closeSheet();
        void load();
      }),
    );
    sheetEl.append(foot);
    return sheetEl;
  }

  function unpairSheet(current: Extract<Sheet, { kind: "unpair" }>): HTMLElement {
    const sheetEl = shell(unpairTitle(current.printer.name), `${ID_PREFIX}-unpair-sheet`);
    if (current.error !== "") sheetEl.append(errorBlock(current.error));
    const foot = node("div", "tablets-sheet-foot");
    const keep = button(
      "Keep",
      "tablets-secondary tablets-sheet-button",
      `${ID_PREFIX}-unpair-keep`,
      () => closeSheet(),
    );
    keep.disabled = current.busy;
    const confirm = button(
      current.busy ? "Unpairing" : "Unpair",
      "tablets-danger",
      `${ID_PREFIX}-unpair-confirm`,
      () => void confirmUnpair(current),
    );
    confirm.disabled = current.busy;
    foot.append(keep, confirm);
    sheetEl.append(foot);
    return sheetEl;
  }

  return {
    async refresh() {
      errorLine = "";
      await load();
    },
    reset,
    dismissSheets: closeSheet,
  };
}
